import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X } from "lucide-react";

export default function Discover() {
  const [intros, setIntros] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/discover/today");
      setIntros(data.intros || []);
      setRemaining(data.remaining_today || 0);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (p, interested) => {
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      const { data } = await api.post("/discover/interest", { other_user_id: p.id, interested });
      if (data.mutual) toast.success("It's mutual! A conversation is now open.");
      else toast(interested ? "Interest sent." : "Passed with kindness.");
      setIntros((cur) => cur.filter((c) => c.id !== p.id));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [p.id]: false })); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-rose">Slow dating</div>
        <h1 className="font-display text-5xl text-navy leading-tight" data-testid="discover-title">Three people. That's enough.</h1>
        <p className="text-navy/70 mt-2 max-w-2xl">
          At Wholesome we don't do endless. We introduce you to three thoughtful people who share your values. Read them slowly.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {intros.length === 0 && (
          <div className="col-span-3 glass rounded-3xl p-10 text-center">
            <div className="font-display text-2xl text-navy">Your three for today are waiting to be discovered.</div>
            <div className="text-navy/60 mt-2">Come back tomorrow — or invite a kind friend to join.</div>
          </div>
        )}
        {intros.map((p) => (
          <div key={p.id} className="glass rounded-3xl p-6 flex flex-col" data-testid={`discover-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-peach to-rose flex items-center justify-center text-white font-display text-xl">
                {(p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="text-right">
                <div className="text-2xl font-display text-sage">{p.compatibility?.score}%</div>
                <div className="text-[10px] uppercase tracking-widest text-navy/60">match</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-display text-2xl text-navy">{p.name}, {p.age}</div>
              <div className="text-sm text-navy/60">{p.city || "Somewhere lovely"}</div>
            </div>
            {p.compatibility?.shared_values?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.compatibility.shared_values.slice(0, 4).map((v) => (
                  <span key={v} className="text-[11px] px-2 py-1 rounded-full bg-rose/10 text-rose">{v}</span>
                ))}
              </div>
            )}
            {p.profile?.story && (
              <div className="mt-4 text-sm text-navy/75 line-clamp-4">{p.profile.story}</div>
            )}
            {p.profile?.personal_motto && (
              <div className="mt-3 text-sm italic text-navy/70">"{p.profile.personal_motto}"</div>
            )}
            <div className="mt-3 text-xs text-navy/50">Photos become visible after mutual interest.</div>
            <div className="mt-auto pt-5 flex gap-3">
              <button data-testid={`pass-${p.id}`} disabled={busy[p.id]} onClick={() => act(p, false)}
                className="flex-1 btn-pill bg-white/70 border border-navy/10 text-navy hover:bg-white flex items-center justify-center gap-2">
                <X size={16} /> Pass
              </button>
              <Link to={`/app/user/${p.id}`} data-testid={`view-${p.id}`}
                className="flex-1 btn-pill bg-white/70 border border-navy/10 text-navy hover:bg-white flex items-center justify-center">
                Read more
              </Link>
              <button data-testid={`like-${p.id}`} disabled={busy[p.id]} onClick={() => act(p, true)}
                className="flex-1 btn-pill bg-rose text-white hover:bg-rose-600 flex items-center justify-center gap-2 hover:scale-[1.02]">
                <Heart size={16} /> Interest
              </button>
            </div>
          </div>
        ))}
      </div>

      {err && <div className="text-sm text-rose-600" data-testid="discover-error">{err}</div>}
    </div>
  );
}
